/** Formatting helpers shared by both surfaces. All UK-locale, GBP. */

const gbp = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const gbpCompact = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

export function formatCurrency(
  value: number | null | undefined,
  options: { compact?: boolean; fallback?: string } = {},
): string {
  const { compact = false, fallback = "—" } = options;
  if (value === null || value === undefined || Number.isNaN(value)) {
    return fallback;
  }
  return compact ? gbpCompact.format(value) : gbp.format(value);
}

export function formatNumber(
  value: number | null | undefined,
  fallback = "—",
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return fallback;
  }
  return new Intl.NumberFormat("en-GB").format(value);
}

export function formatPercent(
  fraction: number | null | undefined,
  fallback = "—",
): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) {
    return fallback;
  }
  return `${(fraction * 100).toFixed(2)}%`;
}

function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  // Cosmos `_ts` is seconds since epoch.
  const date =
    typeof value === "number"
      ? new Date(value < 1e12 ? value * 1000 : value)
      : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(
  value: string | number | Date | null | undefined,
  fallback = "—",
): string {
  const date = parseDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback = "—",
): string {
  const date = parseDate(value);
  if (!date) return fallback;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** "3 minutes ago" / "in 2 days". Used for audit logs and invite expiry. */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
  fallback = "—",
): string {
  const date = parseDate(value);
  if (!date) return fallback;

  const rtf = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
  const diffSeconds = (date.getTime() - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 60 * 60 * 24 * 365],
    ["month", 60 * 60 * 24 * 30],
    ["week", 60 * 60 * 24 * 7],
    ["day", 60 * 60 * 24],
    ["hour", 60 * 60],
    ["minute", 60],
  ];

  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds) {
      return rtf.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return rtf.format(Math.round(diffSeconds), "second");
}

export function formatYesNo(
  value: boolean | string | null | undefined,
  fallback = "—",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") {
    const normalised = value.trim().toLowerCase();
    if (["true", "yes", "y", "1"].includes(normalised)) return "Yes";
    if (["false", "no", "n", "0"].includes(normalised)) return "No";
    return value;
  }
  return value ? "Yes" : "No";
}

/** "vehicleKeptAtSameAddress" → "Vehicle Kept At Same Address" */
export function formatLabel(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

export function toTitleCase(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function fullName(
  parts: Array<string | null | undefined>,
  fallback = "—",
): string {
  const name = parts.filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  return name || fallback;
}

export function formatAddress(
  address:
    | {
        line1?: string | null;
        line2?: string | null;
        street?: string | null;
        house_number?: string | null;
        city?: string | null;
        county?: string | null;
        postcode?: string | null;
        country?: string | null;
      }
    | null
    | undefined,
  fallback = "—",
): string {
  if (!address) return fallback;
  const parts = [
    address.line1 ?? [address.house_number, address.street].filter(Boolean).join(" "),
    address.line2,
    address.city,
    address.county,
    address.postcode,
    address.country,
  ]
    .map((part) => (typeof part === "string" ? part.trim() : part))
    .filter(Boolean);
  return parts.length ? parts.join(", ") : fallback;
}

/** "1m 24s" for the polling countdown. */
export function formatDuration(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`;
}

export function initialsOf(name: string | null | undefined, email?: string | null): string {
  const source = (name ?? "").trim() || (email ?? "").split("@")[0] || "";
  const words = source.split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  pound: "£",
  euro: "€",
  cent: "¢",
  copy: "©",
  reg: "®",
  trade: "™",
  hellip: "…",
  ndash: "–",
  mdash: "—",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  deg: "°",
  times: "×",
};

/**
 * Turns provider-supplied markup into plain text.
 *
 * Broker responses (Kinetiq especially) carry policy conditions as HTML
 * fragments — `<strong>&pound;500</strong> standard excess`, sometimes with
 * `<!-- 1001 -->` build comments still in them. Rendering that raw shows the
 * tags to the operator; rendering it as HTML would mean trusting a third party
 * with `dangerouslySetInnerHTML` on an authenticated page. Stripping to text is
 * the only option that is both readable and safe.
 */
export function htmlToPlainText(value: string | null | undefined): string {
  if (!value) return "";

  return (
    value
      // Build comments leak through in some Kinetiq condition strings.
      .replace(/<!--[\s\S]*?-->/g, " ")
      // Treat block and break tags as whitespace so words do not run together.
      .replace(/<\s*(br|\/p|\/div|\/li|\/tr)\s*\/?\s*>/gi, " ")
      .replace(/<[^>]*>/g, "")
      .replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
        String.fromCodePoint(Number.parseInt(hex, 16)),
      )
      .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number.parseInt(dec, 10)))
      .replace(/&([a-z]+);/gi, (match, name: string) => {
        const replacement = NAMED_ENTITIES[name.toLowerCase()];
        return replacement ?? match;
      })
      .replace(/\s+/g, " ")
      .trim()
  );
}

/** Same, for the string arrays providers use for conditions and endorsements. */
export function htmlListToPlainText(
  values: string[] | null | undefined,
): string[] {
  return (values ?? [])
    .map((value) => htmlToPlainText(value))
    .filter((value) => value.length > 0);
}
